import * as yup from 'yup';
import { StoryCard } from '@web/components';
import { shared } from '../../config';
import {
  nullableImageFromCMSSchema,
  requiredImageFromCMSSchema,
} from '../schemas';
import { resolveVideoPosterUrl } from '../resolveVideoPosterUrl';
import { schema as muxVideoSchema } from './mux_video';

const normalizeTarget = target => {
  if (!target) {
    return undefined;
  }

  if (
    target.startsWith('http') ||
    target.startsWith('mailto:') ||
    target.startsWith('tel:') ||
    target.startsWith('sms:')
  ) {
    return target;
  }

  let path = target;
  if (!path.startsWith('/')) {
    path = `/${path}`;
  }

  const url = new URL(path, `https://www.${shared.SITE_NAME}`);
  if (!url.pathname.endsWith('/')) {
    url.pathname += '/';
  }

  return url.pathname + url.search + url.hash;
};

const transformHandleTarget = (handleTarget, opensNewTab) => {
  const raw = handleTarget?.cached_url || handleTarget?.url;
  if (!raw) {
    return undefined;
  }

  const anchor = handleTarget?.anchor;
  const target = normalizeTarget(raw) + (anchor ? `#${anchor}` : '');

  return {
    target,
    openNewTab: Boolean(opensNewTab ?? handleTarget?.target === '_blank'),
  };
};

const buildVideoThumbnail = ({ videoThumbnail, muxVideo }) => {
  const storyCardThumbnail = resolveVideoPosterUrl(videoThumbnail?.src);
  if (storyCardThumbnail) {
    return storyCardThumbnail;
  }

  const muxThumbnail = resolveVideoPosterUrl(muxVideo?.thumbnail);
  if (muxThumbnail) {
    return muxThumbnail;
  }

  if (muxVideo?.playbackId) {
    return `https://image.mux.com/${muxVideo.playbackId}/thumbnail.png`;
  }

  return undefined;
};

export const component = StoryCard;

export const schema = yup
  .object({
    mediaType: yup.string().oneOf(['video', 'image']),
    quote: yup.string().required(),
    attribution: yup.string().required(),
    modalOnClick: yup.boolean(),
    video: yup.mixed().nullable(),
    image: yup.mixed().nullable(),
    pillText: yup.string().nullable(),
    pillVariant: yup.string().nullable(),
    pillIcon: yup.mixed().nullable(),
    pillIconAlignment: yup.string().nullable(),
    handleText: yup.string().nullable(),
    handleTarget: yup.mixed().nullable(),
  })
  .transform(value => {
    const mediaType =
      value.media_type ||
      (value.image && !value.video?.[0] ? 'image' : 'video');
    const videoBlok = value.video?.[0];
    const videoThumbnail = value.video_thumbnail
      ? nullableImageFromCMSSchema.cast(value.video_thumbnail)
      : undefined;
    const muxVideo =
      mediaType === 'video' && videoBlok
        ? muxVideoSchema.cast(videoBlok)
        : undefined;

    return {
      mediaType,
      quote: value.quote,
      attribution: value.attribution,
      modalOnClick:
        value.modal_on_click ?? (mediaType === 'video' ? true : false),
      video: muxVideo
        ? {
            ...muxVideo,
            thumbnail: buildVideoThumbnail({ videoThumbnail, muxVideo }),
            thumbnailAlt: videoThumbnail?.alt,
          }
        : undefined,
      image:
        mediaType === 'image' && value.image
          ? requiredImageFromCMSSchema.cast(value.image)
          : undefined,
      pillText: value.pill_text || undefined,
      pillVariant: value.pill_variant || undefined,
      pillIcon: value.pill_icon
        ? nullableImageFromCMSSchema.cast(value.pill_icon)
        : undefined,
      pillIconAlignment: value.pill_icon_alignment || undefined,
      handleText: value.handle_text || undefined,
      handleTarget: transformHandleTarget(
        value.handle_target,
        value.handle_opens_new_tab,
      ),
    };
  });
